// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useAdminToast } from './useAdminToast';
import { SettingsCard } from './SettingsCard';

interface BrandingSettingsProps {
  session: any;
  profile: any;
}

interface BrandingAsset {
  key: 'logo' | 'favicon' | 'header' | 'color_scheme' | 'font_settings';
  title: string;
  description: string;
  accept?: string;
  recommendedSize: string;
  maxSize: string;
  previewClass?: string;
  icon: string;
  type: 'image' | 'color' | 'font';
}

// Enhanced color scheme interface
interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  // Additional color options for more creative control
  primaryHover: string;
  secondaryHover: string;
  borderColor: string;
  cardBackground: string;
  headerBackground: string;
}

// Font settings interface
interface FontSettings {
  primaryFont: string;
  secondaryFont: string;
  baseFontSize: string;
  headingFontWeight: string;
  bodyFontWeight: string;
}

export const AdminBrandingSettings: React.FC<BrandingSettingsProps> = ({ session, profile }) => {
  const { showSuccess, showError } = useAdminToast();
  const [currentAssets, setCurrentAssets] = useState<Record<string, string>>({});
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [dragActive, setDragActive] = useState<Record<string, boolean>>({});
  const [colorScheme, setColorScheme] = useState<ColorScheme>({
    primary: '#4F46E5',
    secondary: '#10B981',
    accent: '#F59E0B',
    background: '#FFFFFF',
    text: '#1F2937',
    primaryHover: '#4338CA',
    secondaryHover: '#059669',
    borderColor: '#E5E7EB',
    cardBackground: '#FFFFFF',
    headerBackground: '#F9FAFB'
  });
  const [fontSettings, setFontSettings] = useState<FontSettings>({
    primaryFont: 'Inter, sans-serif',
    secondaryFont: 'Inter, sans-serif',
    baseFontSize: '16px',
    headingFontWeight: '700',
    bodyFontWeight: '400'
  });
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Branding assets configuration
  const brandingAssets: BrandingAsset[] = [
    {
      key: 'logo',
      title: 'Logo',
      description: 'Upload your company logo',
      accept: 'image/svg+xml,image/png,image/jpeg,image/webp',
      recommendedSize: '200×60 pixels (horizontal) or 60×60 pixels (square)',
      maxSize: '2MB',
      previewClass: 'max-h-32',
      icon: 'upload',
      type: 'image'
    },
    {
      key: 'favicon',
      title: 'Favicon',
      description: 'Upload your favicon',
      accept: 'image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml',
      recommendedSize: '32×32 pixels',
      maxSize: '1MB',
      previewClass: 'w-16 h-16',
      icon: 'stars',
      type: 'image'
    },
    {
      key: 'header',
      title: 'Header Image',
      description: 'Upload a header image for the application (optional)',
      accept: 'image/png,image/jpeg,image/webp',
      recommendedSize: '1200×400 pixels',
      maxSize: '2MB',
      previewClass: 'max-h-40',
      icon: 'landscape',
      type: 'image'
    },
    {
      key: 'color_scheme',
      title: 'Color Scheme',
      description: 'Customize your application color palette',
      recommendedSize: 'N/A',
      maxSize: 'N/A',
      icon: 'palette',
      type: 'color'
    },
    {
      key: 'font_settings',
      title: 'Typography',
      description: 'Customize fonts and text styles',
      recommendedSize: 'N/A',
      maxSize: 'N/A',
      icon: 'text_fields',
      type: 'font'
    }
  ];

  // Load current branding assets
  useEffect(() => {
    loadCurrentAssets();
  }, []);

  const loadCurrentAssets = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['logo_url', 'favicon_url', 'header_url', 'color_scheme', 'font_settings']);
      
      if (error) throw error;
      
      const assets: Record<string, string> = {};
      data.forEach(item => {
        if (item.key === 'color_scheme') {
          try {
            const colors = JSON.parse(item.value);
            setColorScheme(prev => ({ ...prev, ...colors }));
          } catch (e) {
            console.error('Error parsing color scheme:', e);
          }
        } else if (item.key === 'font_settings') {
          try {
            const fonts = JSON.parse(item.value);
            setFontSettings(prev => ({ ...prev, ...fonts }));
          } catch (e) {
            console.error('Error parsing font settings:', e);
          }
        } else {
          assets[item.key.replace('_url', '')] = item.value;
        }
      });
      
      setCurrentAssets(assets);
    } catch (err) {
      console.error('Error loading current assets:', err);
    }
  };

  const validateFile = async (file: File, asset: BrandingAsset): Promise<boolean> => {
    // Validate file type
    if (asset.accept) {
      const allowedTypes = asset.accept.split(',');
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`Invalid file type. Please upload ${asset.accept.split(',').map(type => type.split('/')[1].toUpperCase()).join(', ')} files.`);
      }
    }
    
    // Validate file size
    const maxSizeBytes = asset.maxSize === '1MB' ? 1024 * 1024 : 2 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      throw new Error(`File size too large. Maximum size is ${asset.maxSize}.`);
    }
    
    // Validate dimensions for images (except SVG)
    if (asset.key === 'logo' && file.type !== 'image/svg+xml') {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      await new Promise((resolve, reject) => {
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          // Check if dimensions are reasonable
          if (img.width > 500 || img.height > 200) {
            reject(new Error('Logo dimensions too large. Maximum size is 500×200 pixels.'));
          }
          resolve(null);
        };
        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          reject(new Error('Invalid image file.'));
        };
      });
    }
    
    return true;
  };

  const handleFileUpload = async (file: File, asset: BrandingAsset) => {
    try {
      setUploading(prev => ({ ...prev, [asset.key]: true }));
      
      // Validate file
      await validateFile(file, asset);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviews(prev => ({ ...prev, [asset.key]: e.target?.result as string }));
      };
      reader.readAsDataURL(file);
      
      // Upload to Supabase Storage
      const fileExtension = file.type.split('/')[1] || 'png';
      const fileName = `${asset.key}-${Date.now()}.${fileExtension}`;
      const { data, error } = await supabase.storage
        .from('branding')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (error) throw error;
      
      // Update settings in database
      const { error: updateError } = await supabase
        .from('settings')
        .upsert({
          key: `${asset.key}_url`,
          value: data.path,
          updated_at: new Date().toISOString()
        });
      
      if (updateError) throw updateError;
      
      // Update current assets
      setCurrentAssets(prev => ({ ...prev, [asset.key]: data.path }));
      
      showSuccess(`${asset.title} uploaded successfully!`);
    } catch (err: any) {
      console.error(`Error uploading ${asset.key}:`, err);
      showError(err.message || `Failed to upload ${asset.title}`);
    } finally {
      setUploading(prev => ({ ...prev, [asset.key]: false }));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>, asset: BrandingAsset) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, asset);
    }
  };

  const triggerFileInput = (assetKey: string) => {
    fileInputRefs.current[assetKey]?.click();
  };

  const handleDrag = (e: React.DragEvent, assetKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(prev => ({ ...prev, [assetKey]: true }));
    } else if (e.type === 'dragleave') {
      setDragActive(prev => ({ ...prev, [assetKey]: false }));
    }
  };

  const handleDrop = (e: React.DragEvent, asset: BrandingAsset) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [asset.key]: false }));
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0], asset);
    }
  };

  const handleColorChange = (colorKey: keyof ColorScheme, value: string) => {
    setColorScheme(prev => ({
      ...prev,
      [colorKey]: value
    }));
  };

  const handleFontChange = (fontKey: keyof FontSettings, value: string) => {
    setFontSettings(prev => ({
      ...prev,
      [fontKey]: value
    }));
  };

  const saveColorScheme = async () => {
    try {
      setUploading(prev => ({ ...prev, color_scheme: true }));
      
      // Save to database
      const { error } = await supabase
        .from('settings')
        .upsert({
          key: 'color_scheme',
          value: JSON.stringify(colorScheme),
          updated_at: new Date().toISOString()
        });
      
      if (error) throw error;
      
      // Apply colors to CSS variables
      applyColorSchemeToCSS(colorScheme);
      
      showSuccess('Color scheme saved successfully!');
    } catch (err: any) {
      console.error('Error saving color scheme:', err);
      showError(err.message || 'Failed to save color scheme');
    } finally {
      setUploading(prev => ({ ...prev, color_scheme: false }));
    }
  };

  const saveFontSettings = async () => {
    try {
      setUploading(prev => ({ ...prev, font_settings: true }));
      
      // Save to database
      const { error } = await supabase
        .from('settings')
        .upsert({
          key: 'font_settings',
          value: JSON.stringify(fontSettings),
          updated_at: new Date().toISOString()
        });
      
      if (error) throw error;
      
      // Apply fonts to CSS variables
      applyFontSettingsToCSS(fontSettings);
      
      showSuccess('Font settings saved successfully!');
    } catch (err: any) {
      console.error('Error saving font settings:', err);
      showError(err.message || 'Failed to save font settings');
    } finally {
      setUploading(prev => ({ ...prev, font_settings: false }));
    }
  };

  // Apply color scheme to CSS variables
  const applyColorSchemeToCSS = (colors: ColorScheme) => {
    document.documentElement.style.setProperty('--luna-primary', colors.primary);
    document.documentElement.style.setProperty('--luna-secondary', colors.secondary);
    document.documentElement.style.setProperty('--luna-accent', colors.accent);
    document.documentElement.style.setProperty('--luna-background', colors.background);
    document.documentElement.style.setProperty('--luna-text', colors.text);
    document.documentElement.style.setProperty('--luna-primary-hover', colors.primaryHover);
    document.documentElement.style.setProperty('--luna-secondary-hover', colors.secondaryHover);
    document.documentElement.style.setProperty('--luna-border', colors.borderColor);
    document.documentElement.style.setProperty('--luna-card-bg', colors.cardBackground);
    document.documentElement.style.setProperty('--luna-header-bg', colors.headerBackground);
  };

  // Apply font settings to CSS variables
  const applyFontSettingsToCSS = (fonts: FontSettings) => {
    document.documentElement.style.setProperty('--luna-primary-font', fonts.primaryFont);
    document.documentElement.style.setProperty('--luna-secondary-font', fonts.secondaryFont);
    document.documentElement.style.setProperty('--luna-base-font-size', fonts.baseFontSize);
    document.documentElement.style.setProperty('--luna-heading-font-weight', fonts.headingFontWeight);
    document.documentElement.style.setProperty('--luna-body-font-weight', fonts.bodyFontWeight);
  };

  // Preset color schemes
  const presetColorSchemes = [
    {
      name: 'Professional Blue',
      colors: {
        primary: '#2563EB',
        secondary: '#10B981',
        accent: '#F59E0B',
        background: '#FFFFFF',
        text: '#1F2937',
        primaryHover: '#1D4ED8',
        secondaryHover: '#059669',
        borderColor: '#E5E7EB',
        cardBackground: '#FFFFFF',
        headerBackground: '#F9FAFB'
      }
    },
    {
      name: 'Modern Purple',
      colors: {
        primary: '#7C3AED',
        secondary: '#0EA5E9',
        accent: '#F97316',
        background: '#FFFFFF',
        text: '#1F2937',
        primaryHover: '#6D28D9',
        secondaryHover: '#0284C7',
        borderColor: '#E5E7EB',
        cardBackground: '#FFFFFF',
        headerBackground: '#F9FAFB'
      }
    },
    {
      name: 'Warm Earth',
      colors: {
        primary: '#D97706',
        secondary: '#16A34A',
        accent: '#DC2626',
        background: '#FFFFFF',
        text: '#1F2937',
        primaryHover: '#B45309',
        secondaryHover: '#15803D',
        borderColor: '#E5E7EB',
        cardBackground: '#FFFFFF',
        headerBackground: '#F9FAFB'
      }
    },
    {
      name: 'Cool Teal',
      colors: {
        primary: '#0D9488',
        secondary: '#4F46E5',
        accent: '#F59E0B',
        background: '#FFFFFF',
        text: '#1F2937',
        primaryHover: '#0F766E',
        secondaryHover: '#4338CA',
        borderColor: '#E5E7EB',
        cardBackground: '#FFFFFF',
        headerBackground: '#F9FAFB'
      }
    }
  ];

  // Font options
  const fontOptions = [
    { name: 'Inter', value: 'Inter, sans-serif' },
    { name: 'Roboto', value: 'Roboto, sans-serif' },
    { name: 'Open Sans', value: 'Open Sans, sans-serif' },
    { name: 'Lato', value: 'Lato, sans-serif' },
    { name: 'Montserrat', value: 'Montserrat, sans-serif' },
    { name: 'Poppins', value: 'Poppins, sans-serif' },
    { name: 'Nunito', value: 'Nunito, sans-serif' },
    { name: 'Source Sans Pro', value: 'Source Sans Pro, sans-serif' }
  ];

  const renderAssetUploader = (asset: BrandingAsset) => {
    if (asset.type === 'color') {
      return (
        <SettingsCard 
          title={asset.title} 
          description={asset.description}
        >
          <div className="space-y-6">
            {/* Preset Color Schemes */}
            <div>
              <h4 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-3">Preset Color Schemes</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {presetColorSchemes.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => setColorScheme(prev => ({ ...prev, ...preset.colors }))}
                    className="p-3 rounded-lg border border-luna-text-light/20 dark:border-luna-text-dark/30 hover:border-luna-secondary transition-colors text-left"
                  >
                    <div className="flex gap-1 mb-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.colors.primary }}></div>
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.colors.secondary }}></div>
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.colors.accent }}></div>
                    </div>
                    <span className="text-xs font-medium text-luna-text-light dark:text-luna-text-dark">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Color Pickers */}
            <div>
              <h4 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-3">Custom Colors</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {Object.entries(colorScheme).map(([colorKey, colorValue]) => (
                  <div key={colorKey} className="flex flex-col items-center">
                    <label className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2 capitalize">
                      {colorKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={colorValue}
                        onChange={(e) => handleColorChange(colorKey as keyof ColorScheme, e.target.value)}
                        className="w-12 h-12 rounded-lg border border-luna-text-light/20 dark:border-luna-text-dark/30 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={colorValue}
                        onChange={(e) => handleColorChange(colorKey as keyof ColorScheme, e.target.value)}
                        className="w-24 px-2 py-1 text-sm border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={saveColorScheme}
                disabled={uploading.color_scheme}
                className="px-4 py-2 rounded-lg bg-luna-secondary text-white font-medium hover:bg-luna-secondary/90 disabled:opacity-50 min-h-[44px]"
              >
                {uploading.color_scheme ? 'Saving...' : 'Save Colors'}
              </button>
            </div>
            
            <div className="mt-6">
              <h4 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-3">Preview</h4>
              <div 
                className="rounded-lg border border-luna-text-light/20 dark:border-luna-text-dark/30 p-4"
                style={{ backgroundColor: colorScheme.cardBackground }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded flex items-center justify-center"
                    style={{ backgroundColor: colorScheme.primary }}
                  >
                    <span className="material-symbols-outlined text-white">business</span>
                  </div>
                  <span 
                    className="font-bold"
                    style={{ color: colorScheme.text }}
                  >
                    Brand Preview
                  </span>
                </div>
                <div className="space-y-3">
                  <div 
                    className="h-3 rounded-full"
                    style={{ backgroundColor: colorScheme.secondary, width: '80%' }}
                  ></div>
                  <div 
                    className="h-3 rounded-full"
                    style={{ backgroundColor: colorScheme.accent, width: '60%' }}
                  ></div>
                  <div className="flex gap-2 mt-4">
                    <button
                      className="px-3 py-1.5 rounded text-sm font-medium transition-colors"
                      style={{ 
                        backgroundColor: colorScheme.primary,
                        color: '#FFFFFF'
                      }}
                    >
                      Primary
                    </button>
                    <button
                      className="px-3 py-1.5 rounded text-sm font-medium transition-colors"
                      style={{ 
                        backgroundColor: colorScheme.secondary,
                        color: '#FFFFFF'
                      }}
                    >
                      Secondary
                    </button>
                    <button
                      className="px-3 py-1.5 rounded text-sm font-medium border"
                      style={{ 
                        borderColor: colorScheme.borderColor,
                        color: colorScheme.text,
                        backgroundColor: colorScheme.cardBackground
                      }}
                    >
                      Outline
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SettingsCard>
      );
    }
    
    if (asset.type === 'font') {
      return (
        <SettingsCard 
          title={asset.title} 
          description={asset.description}
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                  Primary Font
                </label>
                <select
                  value={fontSettings.primaryFont}
                  onChange={(e) => handleFontChange('primaryFont', e.target.value)}
                  className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                >
                  {fontOptions.map((font) => (
                    <option key={font.value} value={font.value}>{font.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                  Secondary Font
                </label>
                <select
                  value={fontSettings.secondaryFont}
                  onChange={(e) => handleFontChange('secondaryFont', e.target.value)}
                  className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                >
                  {fontOptions.map((font) => (
                    <option key={font.value} value={font.value}>{font.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                  Base Font Size
                </label>
                <select
                  value={fontSettings.baseFontSize}
                  onChange={(e) => handleFontChange('baseFontSize', e.target.value)}
                  className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                >
                  <option value="14px">14px (Small)</option>
                  <option value="15px">15px (Medium)</option>
                  <option value="16px">16px (Default)</option>
                  <option value="17px">17px (Large)</option>
                  <option value="18px">18px (Extra Large)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                  Heading Font Weight
                </label>
                <select
                  value={fontSettings.headingFontWeight}
                  onChange={(e) => handleFontChange('headingFontWeight', e.target.value)}
                  className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                >
                  <option value="500">500 (Medium)</option>
                  <option value="600">600 (Semi Bold)</option>
                  <option value="700">700 (Bold)</option>
                  <option value="800">800 (Extra Bold)</option>
                  <option value="900">900 (Black)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                  Body Font Weight
                </label>
                <select
                  value={fontSettings.bodyFontWeight}
                  onChange={(e) => handleFontChange('bodyFontWeight', e.target.value)}
                  className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                >
                  <option value="300">300 (Light)</option>
                  <option value="400">400 (Regular)</option>
                  <option value="500">500 (Medium)</option>
                  <option value="600">600 (Semi Bold)</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={saveFontSettings}
                disabled={uploading.font_settings}
                className="px-4 py-2 rounded-lg bg-luna-secondary text-white font-medium hover:bg-luna-secondary/90 disabled:opacity-50 min-h-[44px]"
              >
                {uploading.font_settings ? 'Saving...' : 'Save Fonts'}
              </button>
            </div>
            
            <div className="mt-6">
              <h4 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-3">Preview</h4>
              <div 
                className="rounded-lg border border-luna-text-light/20 dark:border-luna-text-dark/30 p-4"
                style={{ 
                  backgroundColor: colorScheme.cardBackground,
                  fontFamily: fontSettings.primaryFont,
                  fontSize: fontSettings.baseFontSize
                }}
              >
                <h3 
                  className="font-bold mb-2"
                  style={{ 
                    color: colorScheme.text,
                    fontWeight: fontSettings.headingFontWeight
                  }}
                >
                  Heading Text Preview
                </h3>
                <p 
                  className="mb-3"
                  style={{ 
                    color: colorScheme.text,
                    fontWeight: fontSettings.bodyFontWeight
                  }}
                >
                  This is a sample paragraph showing how your body text will appear with the selected font settings. 
                  You can see how the font size, weight, and family affect the readability and overall appearance.
                </p>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1.5 rounded text-sm font-medium transition-colors"
                    style={{ 
                      backgroundColor: colorScheme.primary,
                      color: '#FFFFFF',
                      fontFamily: fontSettings.primaryFont,
                      fontWeight: fontSettings.headingFontWeight
                    }}
                  >
                    Primary Button
                  </button>
                  <button
                    className="px-3 py-1.5 rounded text-sm font-medium border"
                    style={{ 
                      borderColor: colorScheme.borderColor,
                      color: colorScheme.text,
                      backgroundColor: colorScheme.cardBackground,
                      fontFamily: fontSettings.primaryFont,
                      fontWeight: fontSettings.bodyFontWeight
                    }}
                  >
                    Secondary Button
                  </button>
                </div>
              </div>
            </div>
          </div>
        </SettingsCard>
      );
    }
    
    const preview = previews[asset.key] || currentAssets[asset.key];
    
    return (
      <SettingsCard 
        title={asset.title} 
        description={`${asset.description} (Recommended size: ${asset.recommendedSize})`}
      >
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-1">
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${dragActive[asset.key] 
                    ? 'border-luna-secondary bg-luna-secondary/5' 
                    : 'border-luna-secondary/30 hover:border-luna-secondary/50'}
                `}
                onClick={() => triggerFileInput(asset.key)}
                onDragEnter={(e) => handleDrag(e, asset.key)}
                onDragOver={(e) => handleDrag(e, asset.key)}
                onDragLeave={(e) => handleDrag(e, asset.key)}
                onDrop={(e) => handleDrop(e, asset)}
              >
                {preview ? (
                  <div className="relative group">
                    <img 
                      src={preview} 
                      alt={`${asset.title} preview`} 
                      className={asset.previewClass} 
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentAssets(prev => {
                            const newAssets = { ...prev };
                            delete newAssets[asset.key];
                            return newAssets;
                          });
                          setPreviews(prev => {
                            const newPreviews = { ...prev };
                            delete newPreviews[asset.key];
                            return newPreviews;
                          });
                        }}
                        className="p-2 bg-red-500 rounded-full text-white"
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="mx-auto w-12 h-12 rounded-full bg-luna-secondary/10 flex items-center justify-center">
                      <span className="material-symbols-outlined text-luna-secondary">{asset.icon}</span>
                    </div>
                    <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
                      Click or drag to upload {asset.title.toLowerCase()}
                    </p>
                    <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                      {asset.accept?.split(',').map(type => type.split('/')[1].toUpperCase()).join(', ')} (max {asset.maxSize})<br />
                      <span className="font-medium">Recommended size:</span> {asset.recommendedSize}
                    </p>
                    {dragActive[asset.key] && (
                      <p className="text-xs font-medium text-luna-secondary">Drop file here</p>
                    )}
                  </div>
                )}
              </div>
              <input
                ref={(el) => (fileInputRefs.current[asset.key] = el)}
                type="file"
                accept={asset.accept}
                onChange={(e) => handleFileInputChange(e, asset)}
                className="hidden"
              />
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => triggerFileInput(asset.key)}
                disabled={uploading[asset.key]}
                className="px-4 py-2 rounded-lg bg-luna-secondary text-white font-medium hover:bg-luna-secondary/90 disabled:opacity-50 min-h-[44px]"
              >
                {uploading[asset.key] ? 'Uploading...' : 'Choose File'}
              </button>
              {currentAssets[asset.key] && (
                <button
                  onClick={() => {
                    setCurrentAssets(prev => {
                      const newAssets = { ...prev };
                      delete newAssets[asset.key];
                      return newAssets;
                    });
                    setPreviews(prev => {
                      const newPreviews = { ...prev };
                      delete newPreviews[asset.key];
                      return newPreviews;
                    });
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 min-h-[44px]"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </SettingsCard>
    );
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
          Branding Settings
        </h1>
        <p className="text-sm sm:text-base text-luna-text-light/70 dark:text-luna-text-dark/70">
          Customize your application's branding elements with creative options
        </p>
      </div>

      {brandingAssets.map(asset => (
        <div key={asset.key}>
          {renderAssetUploader(asset)}
        </div>
      ))}

      <div className="bg-gradient-to-r from-luna-secondary/10 to-blue-500/10 border border-luna-secondary/30 rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <h3 className="font-bold text-lg text-luna-text-light dark:text-luna-text-dark mb-2">
              Branding Preview
            </h3>
            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-4">
              See how your branding will appear in the application
            </p>
            <div className="bg-white dark:bg-luna-primary rounded-lg border border-luna-secondary/30 p-4 max-w-md">
              <div className="flex items-center gap-3 mb-4">
                {previews.logo || currentAssets.logo ? (
                  <img 
                    src={previews.logo || currentAssets.logo} 
                    alt="Logo preview" 
                    className="h-8"
                  />
                ) : (
                  <div 
                    className="bg-luna-secondary/10 w-8 h-8 rounded flex items-center justify-center"
                    style={{ backgroundColor: colorScheme.primary + '20' }}
                  >
                    <span 
                      className="material-symbols-outlined text-sm"
                      style={{ color: colorScheme.primary }}
                    >
                      business
                    </span>
                  </div>
                )}
                <span 
                  className="font-bold text-luna-text-light dark:text-luna-text-dark"
                  style={{ color: colorScheme.text }}
                >
                  LegalRadar
                </span>
              </div>
              <div 
                className="h-2 rounded-full mb-4"
                style={{ backgroundColor: colorScheme.secondary }}
              ></div>
              <div className="space-y-2">
                <div 
                  className="h-3 rounded"
                  style={{ backgroundColor: colorScheme.accent + '40' }}
                ></div>
                <div 
                  className="h-3 rounded w-3/4"
                  style={{ backgroundColor: colorScheme.accent + '40' }}
                ></div>
                <div 
                  className="h-3 rounded w-1/2"
                  style={{ backgroundColor: colorScheme.accent + '40' }}
                ></div>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg text-luna-text-light dark:text-luna-text-dark mb-2">
              Creative Tips
            </h3>
            <ul className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 space-y-3">
              <li className="flex items-start gap-2">
                <span 
                  className="material-symbols-outlined text-sm mt-0.5"
                  style={{ color: colorScheme.primary }}
                >
                  palette
                </span>
                <span>Choose colors that reflect your brand personality</span>
              </li>
              <li className="flex items-start gap-2">
                <span 
                  className="material-symbols-outlined text-sm mt-0.5"
                  style={{ color: colorScheme.primary }}
                >
                  lightbulb
                </span>
                <span>Use simple, recognizable logos for better recall</span>
              </li>
              <li className="flex items-start gap-2">
                <span 
                  className="material-symbols-outlined text-sm mt-0.5"
                  style={{ color: colorScheme.primary }}
                >
                  contrast
                </span>
                <span>Ensure proper contrast between text and background</span>
              </li>
              <li className="flex items-start gap-2">
                <span 
                  className="material-symbols-outlined text-sm mt-0.5"
                  style={{ color: colorScheme.primary }}
                >
                  visibility
                </span>
                <span>Test your favicon on different browser tabs</span>
              </li>
              <li className="flex items-start gap-2">
                <span 
                  className="material-symbols-outlined text-sm mt-0.5"
                  style={{ color: colorScheme.primary }}
                >
                  landscape
                </span>
                <span>Use high-quality header images that tell your story</span>
              </li>
              <li className="flex items-start gap-2">
                <span 
                  className="material-symbols-outlined text-sm mt-0.5"
                  style={{ color: colorScheme.primary }}
                >
                  text_fields
                </span>
                <span>Select fonts that match your brand's tone and style</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="material-symbols-outlined text-blue-500 dark:text-blue-400 mt-0.5">info</span>
          <div>
            <h3 className="font-medium text-blue-800 dark:text-blue-200 mb-1">Advanced Branding Guidelines</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              For best results, use vector formats (SVG) for logos. 
              Favicons should be 32×32 pixels. 
              Header images should be 1200×400 pixels for optimal display.
              Choose a consistent color palette that aligns with your brand identity.
              Select fonts that are web-safe and readable across all devices.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};